import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { PlanRoleBadges } from "@/components/PlanRoleBadges";
import { ShieldAlert, Loader2, LogOut } from "lucide-react";
import { toast } from "sonner";

/**
 * Red banner shown while viewing another account — either an admin "Einblick"
 * or an org-manager "Konto öffnen". Provides a clear exit back to the origin.
 */
export default function ImpersonationBanner() {
  const { user, impersonating, impersonationKind, stopImpersonation } = useAuth();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);

  if (!impersonating) return null;

  const isOrg = impersonationKind === "org";
  const exitLabel = "Zurück zur Übersicht";
  const displayName =
    [user?.name, user?.company].filter(Boolean).join(" | ") || impersonating;

  const handleExit = async () => {
    setBusy(true);
    try {
      const u = await stopImpersonation();
      if (u?.role === "admin") {
        navigate("/administration");
      } else {
        navigate("/organisation");
      }
    } catch {
      toast.error("Wechsel zurück fehlgeschlagen.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="sticky top-0 z-50 bg-red-600 text-white"
      data-testid="impersonation-banner"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-2 flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 text-sm font-medium min-w-0">
          <ShieldAlert className="h-4 w-4 shrink-0" />
          <span className="truncate">
            {isOrg ? "Geöffnetes Konto von" : "Sie befinden sich im Account von"}{" "}
            <span className="font-semibold" data-testid="impersonation-email">
              {displayName}
            </span>
          </span>
          {(user?.plan || user?.rolle) && (
            <PlanRoleBadges plan={user?.plan} rolle={user?.rolle} className="shrink-0" />
          )}
        </div>
        <button
          type="button"
          onClick={handleExit}
          disabled={busy}
          data-testid="exit-impersonation-button"
          className="shrink-0 inline-flex items-center gap-2 rounded-md bg-white/15 hover:bg-white/25 px-3 py-1.5 text-sm font-semibold transition-colors disabled:opacity-60"
        >
          {busy ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <LogOut className="h-4 w-4" />
          )}
          {exitLabel}
        </button>
      </div>
    </div>
  );
}
